import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n/hooks";
import { useLocation } from "react-router-dom";
import { cn } from "../../client/cn";
import MenuButton from "../../components/MenuButton";
import MenuButtonGroup from "../../components/MenuButtonGroup";
import { useDebugMode } from "../../shared/hooks";
import UsageTable from './UsageTable';
import {
  AlertCircle,
  CreditCard,
  BotMessageSquare,
  ClipboardList,
  Blocks,
  Binoculars
} from 'lucide-react';
import { useAuth } from "wasp/client/auth";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);
  const isDebugMode = useDebugMode();
  const { data: user } = useAuth();
  const [openGroups, setOpenGroups] = useState<string[]>(['content-quality']);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/optimization')) {
      setOpenGroups(['content-optimization']);
    } else if (path.includes('/dashboard') || path.includes('/products') || path.includes('/jobs')) {
      setOpenGroups(['content-quality']);
    }
  }, [location.pathname]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => {
      const wasOpen = prev.includes(groupId);
      if (wasOpen) {
        return [];
      }
      return [groupId];
    });
  };

  const handleStandaloneClick = () => {
    setOpenGroups([]);
  };

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <aside
      ref={sidebar}
      className={cn(
        "absolute left-0 top-0 z-9 flex pt-[80px] md:pt-0 h-full md:h-[calc(100%-4rem)] md:h-screen w-60 bg-white border-r border-gray-200 dark:bg-gray-900 dark:border-gray-800 lg:static lg:translate-x-0",
        {
          "translate-x-0": sidebarOpen,
          "-translate-x-full": !sidebarOpen,
        }
      )}
    >
      <div className="relative flex flex-col w-full h-full">
        <div className="flex-1 overflow-y-auto pb-32">
          <nav className="px-2 py-2 space-y-1">
            {/* Get Started (New) */}
            {/* <MenuButton
              to="/admin/get-started"
              icon={<Sparkles size={16} />}
              onClick={handleStandaloneClick}
            >
              Get Started
            </MenuButton> */}

            {/* Content Quality Group */}
            <MenuButtonGroup
              title={t('sidebar.content_quality')}
              isOpen={openGroups.includes('content-quality')}
              icon={<ClipboardList size={16} />}
              defaultPath="/admin/dashboard"
              groupId="content-quality"
              onToggle={toggleGroup}
            >
              <MenuButton
                to="/admin/dashboard"
                isSubItem
              >
                {t('sidebar.quality_dashboard')}
              </MenuButton>
              <MenuButton
                to="/admin/products"
                isSubItem
              >
                {t('sidebar.products')}
              </MenuButton>
              <MenuButton
                to="/admin/jobs"
                isSubItem
              >
                {t('sidebar.jobs')}
              </MenuButton>
            </MenuButtonGroup>

            {/* Content Optimization Group */}
            {isDebugMode && (
              <MenuButtonGroup
                title={t('sidebar.content_optimization')}
                isOpen={openGroups.includes('content-optimization')}
                icon={<Blocks size={16} />}
                defaultPath="/admin/optimization/dashboard"
                groupId="content-optimization"
                onToggle={toggleGroup}
              >
                <MenuButton
                  to="/admin/optimization/dashboard"
                  isSubItem
                >
                  {t('sidebar.dashboard')}
                </MenuButton>
                <MenuButton
                  to="/admin/optimization/testing"
                  isSubItem
                >
                  {t('sidebar.testing')}
                </MenuButton>
              </MenuButtonGroup>
            )}

            {/* Settings & Debug */}
            <MenuButton
              to="/admin/upgrade-plan"
              icon={<CreditCard size={16} />}
              onClick={handleStandaloneClick}
            >
              {t('sidebar.upgrade_plan')}
            </MenuButton>

            {isDebugMode && (
              <>
                <MenuButton
                  to="/admin/debug"
                  icon={<AlertCircle size={16} />}
                  onClick={handleStandaloneClick}
                >
                  {t('sidebar.debug')}
                </MenuButton>
                <MenuButton
                  to="/admin/gengage-settings"
                  icon={<BotMessageSquare size={16} />}
                  onClick={handleStandaloneClick}
                >
                  {t('sidebar.gengage_debug')}
                </MenuButton>
                {(user?.email === "dogukan@glov.ai" || user?.email === "alper@glov.ai") && <MenuButton
                  to="/admin/users-collections"
                  icon={<Binoculars size={16} />}
                  onClick={handleStandaloneClick}
                >
                  {t('sidebar.spyview')}
                </MenuButton>}
              </>
            )}
          </nav>
        </div>

        {/* Usage Table */}
        <div className="absolute bottom-0 md:bottom-[74px] left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
          <div className="p-4">
            <UsageTable />
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;